import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRightIcon, MailIcon, PackageSearchIcon, ShieldCheckIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { FormField, inputClasses } from '../../components/ui/FormField';
import { BrandLogo } from '../../components/shop/BrandLogo';
import { buttonClasses } from '../../utils/buttonStyles';
import { isLikelyUspsNumber, parseTrackingNumbers, uspsTrackingUrl } from '../../utils/tracking';
import { shippingCarriers } from '../../data/shopify';
import { track } from '../../utils/analytics';
const usps = shippingCarriers.find(c => c.id === 'usps');
export function OrderTracking() {
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [opened, setOpened] = useState<string | null>(null);
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const numbers = parseTrackingNumbers(value);
    if (!numbers.length) {
      setError('Enter the USPS tracking number from your shipping confirmation email.');
      return;
    }
    const invalid = numbers.filter(n => !isLikelyUspsNumber(n));
    if (invalid.length) {
      setError(`"${invalid[0]}" doesn't look like a USPS tracking number. USPS numbers are usually 20–22 digits (e.g. 9400 1000 0000 0000 0000 00).`);
      return;
    }
    setError('');
    const url = uspsTrackingUrl(numbers);
    track('order_tracking_lookup', {
      carrier: 'usps',
      count: numbers.length
    });
    const win = window.open(url, '_blank', 'noopener,noreferrer');
    setOpened(win ? null : url);
  };
  return <>
      <PageHeader title="Track Your Order" lead="Every MarineForceLED order ships with USPS. Enter your tracking number to see live delivery status." data-id="element-1402" data-mp="375h0sownpqtx" />
      <div className="mx-auto grid max-w-site gap-10 px-5 py-12 md:px-8 md:py-16 lg:grid-cols-[1.4fr_1fr] lg:gap-20" data-id="element-1403" data-mp="2emk3copjg35m">
        <div data-id="element-1404" data-mp="29oi8l6ir2feb">
          <form noValidate onSubmit={submit} className="mf-hud border border-marine/30 bg-[#06121F] p-6 md:p-8" data-id="element-1405" data-mp="29mpboixxv9ji">
            <div className="flex items-center gap-3" data-id="element-1406" data-mp="ikqg39zscwn8">
              {usps && <BrandLogo src={usps.logo} label={usps.label} className="h-8 w-[118px] bg-white px-1.5 py-1" data-id="element-1407" data-mp="ij5mbmlaphqf" />}
              <span className="text-sm text-titanium" data-id="element-1408" data-mp="nc8f3si90n5k">Official USPS tracking</span>
            </div>

            <div className="mt-6" data-id="element-1409" data-mp="s9qkj8x24k8t">
              <FormField id="ot-number" label="USPS tracking number" error={error || undefined} data-id="element-1410" data-mp="3qsf9t9iora27">
                <input className={`${inputClasses} font-mono tracking-[0.06em]`} value={value} onChange={e => setValue(e.target.value)} placeholder="9400 1000 0000 0000 0000 00" inputMode="text" autoComplete="off" autoCapitalize="characters" spellCheck={false} data-id="element-1411" data-mp="3vr0996q45ppl" />
              </FormField>
              <p className="mt-2 text-xs text-titanium/70" data-id="element-1412" data-mp="1k04pugr08o8v">Have more than one package? Separate numbers with commas.</p>
            </div>

            <button type="submit" className={`${buttonClasses('primary')} mt-6 w-full sm:w-auto`} data-id="element-1413" data-mp="30i51gs927ftl">
              Track on USPS <ArrowUpRightIcon className="h-4 w-4" aria-hidden data-id="element-1414" data-mp="2vqwstyfjzyhh" />
            </button>
            <p className="mt-3 text-xs text-titanium/70" data-id="element-1415" data-mp="2qsd4xshegcmh">Opens USPS.com in a new tab.</p>

            {opened && <p role="status" className="mt-5 text-sm text-titanium" data-id="element-1416" data-mp="2lts53u627uyk">
                Your browser blocked the new tab.{' '}
                <a href={opened} target="_blank" rel="noopener noreferrer" className="font-semibold text-ice underline underline-offset-2" data-id="element-1417" data-mp="2gte6t3u9fzdq">
                  Open USPS tracking
                </a>
              </p>}
          </form>
        </div>

        <aside className="space-y-8" data-id="element-1418" data-mp="b0xnzxtt25j3">
          <div className="flex gap-4" data-id="element-1419" data-mp="azwc5r4ytf0b">
            <MailIcon className="mt-0.5 h-5 w-5 shrink-0 text-marine" aria-hidden data-id="element-1420" data-mp="ayv0plk1eqi2" />
            <div data-id="element-1421" data-mp="62w2l68wpu4g">
              <h2 className="font-display text-lg font-semibold uppercase tracking-[0.06em] text-white" data-id="element-1422" data-mp="60a0hm4sc6tm">Where's my tracking number?</h2>
              <p className="mt-1 text-[15px] text-titanium" data-id="element-1423" data-mp="33vxwukviheve">
                It's in your shipping confirmation email from MarineForceLED, sent as soon as your order leaves our facility. Check your spam or promotions folder too.
              </p>
            </div>
          </div>
          <div className="flex gap-4" data-id="element-1424" data-mp="2g6d9lpsbrvgb">
            <PackageSearchIcon className="mt-0.5 h-5 w-5 shrink-0 text-marine" aria-hidden data-id="element-1425" data-mp="2g5bo0rdonqkc" />
            <div data-id="element-1426" data-mp="2kxv909ep9nz6">
              <h2 className="font-display text-lg font-semibold uppercase tracking-[0.06em] text-white" data-id="element-1427" data-mp="2kyelvbphtm6c">No movement for a while?</h2>
              <p className="mt-1 text-[15px] text-titanium" data-id="element-1428" data-mp="2b70deefkdc12">
                New labels can take up to 24 hours to show in USPS. If tracking hasn't updated in 7 business days,{' '}
                <Link to="/support" className="text-ice underline underline-offset-2" data-id="element-1429" data-mp="1x9c1vx7dzh8o">contact support</Link> and we'll open a trace.
              </p>
            </div>
          </div>
          <div className="flex gap-4" data-id="element-1430" data-mp="1il28m7yn9p5h">
            <ShieldCheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-marine" aria-hidden data-id="element-1431" data-mp="1dlh3ffsvskkb" />
            <div data-id="element-1432" data-mp="1siq6v7vglese">
              <h2 className="font-display text-lg font-semibold uppercase tracking-[0.06em] text-white" data-id="element-1433" data-mp="1sj9dhsfnsgr4">Arrived damaged?</h2>
              <p className="mt-1 text-[15px] text-titanium" data-id="element-1434" data-mp="1nilmv7vce1xf">
                Report it within 7 days of delivery. See our{' '}
                <Link to="/shipping" className="text-ice underline underline-offset-2" data-id="element-1435" data-mp="21hlfsrpw12ze">Shipping Policy</Link>.
              </p>
            </div>
          </div>
        </aside>
      </div>
    </>;
}